import { useState, useRef, useEffect } from "react";
import { API_BASE_URL, WEBSITE_BASE_URL } from "@/constants";
import { inviteCodeFromPath, inviteCodeMeta } from "@/documentMeta";
import { useAPY, writeToClipboard, claimInviteAndOpenApp } from "./inviteShared";
import logo from "../assets/logo-goldsand-v2.png";

// The CF worker validates the code server-side and embeds the result in meta
// tags, so we render success/error on load without a client API request.
// Returns null when there's no definitive answer (worker absent in `bun dev`,
// or the lookup returned "unknown"), in which case we fall back to fetching.
function getServerInviteState(): { valid: boolean; firstName: string | null } | null {
  const status = document.querySelector('meta[name="invite-status"]')?.getAttribute("content");
  if (status !== "valid" && status !== "invalid") return null;
  const firstName = document.querySelector('meta[name="invite-referrer-name"]')?.getAttribute("content");
  return { valid: status === "valid", firstName: firstName && firstName.length > 0 ? firstName : null };
}

export default function InviteCode() {
  const code = inviteCodeFromPath(window.location.pathname);
  const serverState = getServerInviteState();
  const [isCodeValid, setIsCodeValid] = useState<boolean | null>(
    !code ? false : serverState ? serverState.valid : null
  );
  const [referrerFirstName, setReferrerFirstName] = useState<string | null>(serverState?.firstName ?? null);
  const apyDisplay = useAPY();
  const [status, setStatus] = useState<{ text: string; type: "success" | "error" } | null>(null);
  const [disabled, setDisabled] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasValidCode = Boolean(code) && isCodeValid === true;

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!code) return;
    // The worker already embedded a definitive result in meta tags — no API call.
    if (serverState) return;

    let cancelled = false;
    setIsCodeValid(null);
    setReferrerFirstName(null);

    fetch(`${API_BASE_URL}/v3/invite_codes/${encodeURIComponent(code)}`)
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          const data = (await res.json().catch(() => null)) as { referrer_first_name?: unknown } | null;
          setReferrerFirstName(
            data && typeof data.referrer_first_name === "string" ? data.referrer_first_name : null
          );
          setIsCodeValid(true);
        } else {
          setIsCodeValid(false);
        }
      })
      .catch(() => {
        if (cancelled) return;
        setIsCodeValid(false);
      });

    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    document.title = inviteCodeMeta(referrerFirstName).title;
  }, [referrerFirstName]);

  const handleCopyCode = async () => {
    if (!code) return;
    try {
      await writeToClipboard(code);
      setCopied(true);
      if (copiedTimeoutRef.current) clearTimeout(copiedTimeoutRef.current);
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setStatus({ text: "Couldn't copy automatically. You can still select and copy the code.", type: "error" });
    }
  };

  const handleDownload = () => {
    if (!hasValidCode) {
      window.location.href = WEBSITE_BASE_URL;
      return;
    }
    const inviteCode = code;
    if (!inviteCode) return;

    setDisabled(true);
    setStatus({ text: "Opening Goldsand…", type: "success" });

    claimInviteAndOpenApp(inviteCode, () => {
      setStatus(null);
      setDisabled(false);
    });
  };

  return (
    <div className="min-h-[100vh] min-h-dvh flex flex-col bg-background text-foreground">
      {/* Main content — centered */}
      <div className="flex-1 flex flex-col items-center justify-center px-lg py-lg">
        <div className="max-w-[380px] w-full flex flex-col items-center text-center gap-[24px] max-[375px]:gap-[20px]">

          {/* Badge */}
          <span className="inline-flex items-center gap-[8px] bg-foreground text-primary-fg text-base font-semibold px-[18px] py-[10px] rounded-full select-none shadow-[0_2px_8px_rgba(0,0,0,0.1)]">
            <span className="w-[8px] h-[8px] rounded-full bg-accent shadow-[0_0_6px_rgba(194,160,49,0.6)] animate-pulse" />
            Private Invite
          </span>

          {/* Headline + subtitle */}
          <div className="flex flex-col items-center gap-md max-[375px]:gap-sm">
            <h1 className="text-[40px] max-[375px]:text-[34px] font-bold tracking-tight leading-[1.08]">
              {referrerFirstName ? `${referrerFirstName} invited you to` : "You're invited to"}
              <br />
              <span className="inline-flex items-center gap-[8px]">
                <span className="inline-block w-[38px] h-[38px] max-[375px]:w-[32px] max-[375px]:h-[32px] rounded-[22%] shadow-[0_2px_8px_rgba(194,160,49,0.3)] align-middle overflow-hidden">
                  <img
                    src={logo}
                    alt=""
                    className="w-full h-full object-cover scale-[1.18]"
                  />
                </span>
                Goldsand
              </span>
            </h1>
            <p className="text-md text-muted-fg leading-[1.55] max-[375px]:text-base">
              The halal savings account earning up to{" "}
              <span className="text-accent font-bold text-[22px] whitespace-nowrap">{apyDisplay} APY</span> on your USD.
              <br />
              No interest. Pure profit-sharing.
            </p>
          </div>

          {/* Code row */}
          {hasValidCode && (
            <div className="flex items-center gap-sm">
              <span className="text-base text-muted-fg">Your code:</span>
              <span className="text-md font-bold tracking-[0.1em] text-foreground select-all">{code}</span>
              <button
                type="button"
                onClick={handleCopyCode}
                aria-label={copied ? "Invite code copied" : "Copy invite code"}
                className={`w-[28px] h-[28px] rounded-md border transition-colors cursor-pointer flex items-center justify-center ${
                  copied
                    ? "border-success/40 bg-success/10 text-success"
                    : "border-border bg-background text-muted-fg hover:text-foreground hover:bg-secondary"
                }`}
              >
                {copied ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M2.5 7.5L5.5 10.5L11.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                    <path d="M5 2.5H2.8A1.3 1.3 0 001.5 3.8v7.4a1.3 1.3 0 001.3 1.3h5.4a1.3 1.3 0 001.3-1.3V9.8" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    <rect x="5.5" y="1.5" width="7" height="7" rx="1.3" stroke="currentColor" strokeWidth="1.2" />
                  </svg>
                )}
              </button>
              <span
                className={`text-sm font-medium text-success transition-opacity ${copied ? "opacity-100" : "opacity-0"}`}
                role="status"
                aria-live="polite"
              >
                Copied!
              </span>
            </div>
          )}

          {isCodeValid === false && (
            <div className="w-full max-w-[320px] rounded-lg border border-destructive/25 bg-destructive/5 px-md py-sm">
              <p className="text-sm font-semibold text-destructive">Invalid invite link</p>
              <p className="text-sm text-muted-fg mt-[4px] leading-relaxed">
                This link has a missing or invalid invite code. Ask your friend to send a valid invite link again.
              </p>
            </div>
          )}

          {isCodeValid === null && code && (
            <div className="w-full max-w-[320px] rounded-lg border border-border bg-secondary px-md py-sm">
              <p className="text-sm font-medium text-muted-fg">Validating invite code...</p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom CTA — respects safe area on notched/gesture-nav devices */}
      <div className="px-lg pb-[max(24px,env(safe-area-inset-bottom))] pt-md max-w-[380px] w-full mx-auto flex flex-col gap-md">
        <button
          type="button"
          disabled={disabled || isCodeValid === null}
          onClick={handleDownload}
          className="btn-primary h-[56px] max-[375px]:h-[52px] w-full rounded-full bg-foreground text-primary-fg text-[17px] font-semibold cursor-pointer shadow-[0_2px_8px_rgba(0,0,0,0.12)]"
        >
          {hasValidCode ? "Claim Your Invite" : isCodeValid === null ? "Validating..." : "Sign up"}
        </button>
        {status && (
          <p
            className={`text-sm font-medium text-center ${
              status.type === "success" ? "text-success" : "text-destructive"
            }`}
            role="status"
            aria-live="polite"
          >
            {status.text}
          </p>
        )}
        <p className="text-sm text-muted-fg text-center leading-relaxed">
          By downloading you agree to Goldsand's{" "}
          <a href={`${WEBSITE_BASE_URL}/terms-of-use`} target="_blank" rel="noopener noreferrer" className="text-accent underline">Terms&nbsp;of&nbsp;Use</a> and{" "}
          <a href={`${WEBSITE_BASE_URL}/privacy-policy`} target="_blank" rel="noopener noreferrer" className="text-accent underline">Privacy&nbsp;Policy</a>.
        </p>
      </div>
    </div>
  );
}
